/**
 * How a nomination's review status reads, and what can still be done to it.
 *
 * The status machine is the service's, and it is strict: only a PENDING_REVIEW nomination can
 * be decided. Approving an already-approved one is an idempotent no-op, but the OPPOSITE
 * decision on a decided nomination is a 409 — a decision cannot be reversed. So the only
 * honest UI is to offer the actions on pending rows and not on the others.
 */

import type { NominationStatus } from '../client/EventCalendarClient';

export const NOMINATION_STATUS_LABELS: Record<NominationStatus, string> = {
  PENDING_REVIEW: 'Pending Review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

/** Cloudscape StatusIndicator types, one per status. */
export const NOMINATION_STATUS_INDICATOR: Record<
  NominationStatus,
  'pending' | 'success' | 'error'
> = {
  // Pending, not "info": it is waiting on someone, which is the point of the whole page.
  PENDING_REVIEW: 'pending',
  APPROVED: 'success',
  REJECTED: 'error',
};

/**
 * Whether this nomination can still be approved or rejected.
 *
 * A missing status is treated as NOT decidable. `status` is optional on the wire even though
 * the service always sets it, and offering a decision on a row whose state is unknown risks a
 * 409 the user cannot make sense of.
 */
export function isDecidable(status: NominationStatus | undefined): boolean {
  return status === 'PENDING_REVIEW';
}

/** The order the status filter lists them: what needs attention first. */
export const NOMINATION_STATUS_ORDER: NominationStatus[] = [
  'PENDING_REVIEW',
  'APPROVED',
  'REJECTED',
];
